import { RGBColor, CMYKColor, HSLColor, COLOR_CONSTANTS } from '../types/color';

/**
 * Color utility functions for format conversion and validation
 */
export const colorUtils = {
  /**
   * Convert hex color to RGB
   */
  hexToRgb: (hex: string): RGBColor | null => {
    const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
    return result ? {
      r: parseInt(result[1], 16),
      g: parseInt(result[2], 16),
      b: parseInt(result[3], 16)
    } : null;
  },

  /**
   * Convert RGB to hex color
   */
  rgbToHex: (r: number, g: number, b: number): string => {
    const toHex = (n: number) => {
      const hex = Math.round(Math.max(0, Math.min(255, n))).toString(16);
      return hex.length === 1 ? '0' + hex : hex;
    };
    return `#${toHex(r)}${toHex(g)}${toHex(b)}`.toUpperCase();
  },

  /**
   * Convert hex color to HSL
   */
  hexToHsl: (hex: string): HSLColor | null => {
    const rgb = colorUtils.hexToRgb(hex);
    if (!rgb) return null;

    const r = rgb.r / 255;
    const g = rgb.g / 255;
    const b = rgb.b / 255;

    const max = Math.max(r, g, b);
    const min = Math.min(r, g, b);
    let h = 0;
    let s = 0;
    const l = (max + min) / 2;

    if (max !== min) {
      const d = max - min;
      s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
      
      switch (max) {
        case r: h = (g - b) / d + (g < b ? 6 : 0); break;
        case g: h = (b - r) / d + 2; break;
        case b: h = (r - g) / d + 4; break;
      }
      h /= 6;
    }

    return {
      h: Math.round(h * 360),
      s: Math.round(s * 100),
      l: Math.round(l * 100)
    };
  },

  /**
   * Convert HSL to hex color
   */
  hslToHex: (h: number, s: number, l: number): string => {
    h = h / 360;
    s = s / 100;
    l = l / 100;

    const hue2rgb = (p: number, q: number, t: number) => {
      if (t < 0) t += 1;
      if (t > 1) t -= 1;
      if (t < 1/6) return p + (q - p) * 6 * t;
      if (t < 1/2) return q;
      if (t < 2/3) return p + (q - p) * (2/3 - t) * 6;
      return p;
    };

    let r, g, b;

    if (s === 0) {
      r = g = b = l; // achromatic
    } else {
      const q = l < 0.5 ? l * (1 + s) : l + s - l * s;
      const p = 2 * l - q;
      r = hue2rgb(p, q, h + 1/3);
      g = hue2rgb(p, q, h);
      b = hue2rgb(p, q, h - 1/3);
    }

    return colorUtils.rgbToHex(
      Math.round(r * 255),
      Math.round(g * 255),
      Math.round(b * 255)
    );
  },

  /**
   * Convert RGB to CMYK
   */
  rgbToCmyk: (r: number, g: number, b: number): CMYKColor => {
    const rNorm = r / 255;
    const gNorm = g / 255;
    const bNorm = b / 255;

    const k = 1 - Math.max(rNorm, gNorm, bNorm);
    const c = k === 1 ? 0 : (1 - rNorm - k) / (1 - k);
    const m = k === 1 ? 0 : (1 - gNorm - k) / (1 - k);
    const y = k === 1 ? 0 : (1 - bNorm - k) / (1 - k);

    return {
      c: Math.round(c * 100),
      m: Math.round(m * 100),
      y: Math.round(y * 100),
      k: Math.round(k * 100)
    };
  },

  /**
   * Convert CMYK to RGB
   */
  cmykToRgb: (c: number, m: number, y: number, k: number): RGBColor => {
    const cNorm = c / 100;
    const mNorm = m / 100;
    const yNorm = y / 100;
    const kNorm = k / 100;

    const r = 255 * (1 - cNorm) * (1 - kNorm);
    const g = 255 * (1 - mNorm) * (1 - kNorm);
    const b = 255 * (1 - yNorm) * (1 - kNorm);

    return {
      r: Math.round(r),
      g: Math.round(g),
      b: Math.round(b)
    };
  },

  /**
   * Validate hex color format
   */
  isValidHex: (hex: string): boolean => {
    return COLOR_CONSTANTS.HEX_PATTERN.test(hex);
  },

  /**
   * Validate RGB color format
   */
  isValidRgb: (rgb: string): boolean => {
    const match = rgb.match(COLOR_CONSTANTS.RGB_PATTERN);
    if (!match) return false;
    
    const [, r, g, b] = match;
    return [r, g, b].every(val => {
      const num = parseInt(val, 10);
      return num >= 0 && num <= 255;
    });
  },

  /**
   * Validate CMYK color format
   */
  isValidCmyk: (cmyk: string): boolean => {
    const match = cmyk.match(COLOR_CONSTANTS.CMYK_PATTERN);
    if (!match) return false;
    
    const [, c, m, y, k] = match;
    return [c, m, y, k].every(val => {
      const num = parseInt(val, 10);
      return num >= 0 && num <= 100;
    });
  },

  /**
   * Format RGB values as string
   */
  formatRgb: (r: number, g: number, b: number): string => {
    return `${r}, ${g}, ${b}`;
  },

  /**
   * Format CMYK values as string
   */
  formatCmyk: (c: number, m: number, y: number, k: number): string => {
    return `${c}, ${m}, ${y}, ${k}`;
  },

  /**
   * Parse RGB string to RGB object
   */
  parseRgb: (rgb: string): RGBColor | null => {
    const match = rgb.match(COLOR_CONSTANTS.RGB_PATTERN);
    if (!match) return null;
    
    const [, r, g, b] = match;
    return {
      r: parseInt(r, 10),
      g: parseInt(g, 10),
      b: parseInt(b, 10)
    };
  },

  /**
   * Parse CMYK string to CMYK object
   */
  parseCmyk: (cmyk: string): CMYKColor | null => {
    const match = cmyk.match(COLOR_CONSTANTS.CMYK_PATTERN);
    if (!match) return null;
    
    const [, c, m, y, k] = match;
    return {
      c: parseInt(c, 10),
      m: parseInt(m, 10),
      y: parseInt(y, 10),
      k: parseInt(k, 10)
    };
  },

  /**
   * Get contrast color (black or white) for given hex color
   */
  getContrastColor: (hex: string): string => {
    const luminance = colorUtils.getLuminance(hex);
    return luminance > 0.5 ? '#000000' : '#FFFFFF';
  },

  /**
   * Calculate luminance of a hex color
   */
  getLuminance: (hex: string): number => {
    const rgb = colorUtils.hexToRgb(hex);
    if (!rgb) return 0;

    const { r, g, b } = rgb;
    const [rs, gs, bs] = [r, g, b].map(c => {
      c = c / 255;
      return c <= 0.03928 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4);
    });

    return 0.2126 * rs + 0.7152 * gs + 0.0722 * bs;
  },

  /**
   * Get a readable color name for a hex color (simplified)
   */
  getColorName: (hex: string): string => {
    const rgb = colorUtils.hexToRgb(hex);
    if (!rgb) return 'Unknown';

    const { r, g, b } = rgb;
    
    // Simple color name mapping
    if (r > 200 && g < 100 && b < 100) return 'Red';
    if (r < 100 && g > 200 && b < 100) return 'Green';
    if (r < 100 && g < 100 && b > 200) return 'Blue';
    if (r > 200 && g > 200 && b < 100) return 'Yellow';
    if (r > 200 && g < 100 && b > 200) return 'Magenta';
    if (r < 100 && g > 200 && b > 200) return 'Cyan';
    if (r < 50 && g < 50 && b < 50) return 'Black';
    if (r > 200 && g > 200 && b > 200) return 'White';
    if (Math.abs(r - g) < 30 && Math.abs(g - b) < 30) return 'Gray';
    
    return 'Custom';
  }
};
